import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Divider, Paper, Grid, Box, Stack, Tooltip, IconButton, Button
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Modal from 'components/Modal';
import useConfirm from 'core/hooks/useConfirm';
import EditIcon from '@mui/icons-material/Edit';
import AddAdmin from './add';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import { getAdminList, activateAdmin, deactivateAdmin } from 'services/admin';

const AdminUsersPage = () => {

  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }), []);
  const [adminList, setAdminList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [adminInfo, setAdminInfo] = useState({ info: null });

  const onActionBtnClick = useCallback((e, type, adminData) => {
    e.preventDefault();
    if(type === 'edit'){
      editAdminDetails(adminData);
    }
  }, []);

  const defaultFilterValue = [
    { name: 'displayName', operator: 'contains', type: 'string', value: '' },
    { name: 'email', operator: 'contains', type: 'string', value: '' },
    { name: 'mobile', operator: 'contains', type: 'string', value: '' },
    { name: 'createdOn', operator: 'contains', type: 'string', value: '' },
  ];

  const columns = useMemo(() => ([
    { name: 'displayName', header: 'Name', width: 300},
    { name: 'email', header: 'Email', width: 300},
    { name: 'mobile', header: 'Mobile', width: 150},
    { name: 'createdOn', header: 'Created On', width: 150},
    { name: 'actions', header: 'Actions', width: 220, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Edit Admin Details" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'edit', data)}>
                <EditIcon color="warning" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title={data?.active ? 'Deactivate Admin' : 'Activate Admin'} arrow placement="left">
              <Button
                onClick={() => changeUserState(data?.userId, data?.active)} 
                size="small" 
                variant="contained"
                sx={{textTransform: 'none'}}
                color={data?.active ? 'error' : 'success'}
                >
                  {data?.active ? 'Deactivate' : 'Activate'}
              </Button>
            </Tooltip>
          </Box>
        </Stack>
      )
    }},
  ]),[onActionBtnClick]);

  useEffect(() => {
    getAdminData();
  }, []);

  function getAdminData() {
    getAdminList().then(({response, error}) => {
      if(response){
        setAdminList([...(response || [])]);
      } else {
        setAdminList([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const editAdminDetails = (adminData) => {
    setAdminInfo({ info: adminData });
    setTimeout(() => setOpenModal(true), 100);
  }

  const changeUserState = (userId, isActive) => {
    confirm({ title: `${isActive ? 'Deactivate Admin' : 'Activate Admin'}`, description: 'Are you sure you want to proceed?' })
    .then(() => { 
      if(isActive){
        _deactivateAdmin(userId);
      }else{
        _activateAdmin(userId);
      }
    }).catch(() => {});
  }

  function _activateAdmin(userId) {
    activateAdmin({userId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Admin activated successfully", {variant: 'success'});
        getAdminData();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function _deactivateAdmin(userId) {
    deactivateAdmin({userId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Admin deactivated successfully", {variant: 'success'});
        getAdminData();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
    },
    [setOpenModal],
  )

 
  const onSuccess = useCallback(
    () => {
      setOpenModal(false);
      getAdminData();
      setAdminInfo({info: null});
    },
    [setOpenModal],
  )

  function onAddAdminBtnClick() {
    setOpenModal(true);
    setAdminInfo({info: null});
  }

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Admin Users
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Button variant="contained" sx={{textTransform: 'none', mt: 2}} onClick={onAddAdminBtnClick}>Add Admin</Button>
            <Box component="div" sx={{  mt: 2 ,  maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                // handle={setGridRef}
                // idProperty="index"
                style={gridStyle}
                defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={adminList}
                rowHeight={null} 
                minRowHeight={50}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
      <Modal  maxWidth={"sm"} open={openModal} onModalClose={onModalClose} title={`${adminInfo?.info ? 'Edit' : 'Add'} Admin`}>
        <AddAdmin adminData={adminInfo.info} onSuccess={onSuccess} onFailure={onModalClose} />
      </Modal>
    </Paper>
  );
}
 
export default React.memo(AdminUsersPage);
